<!-- Le voci di una sezione, con lo stesso markup nelle due viste (sezione sempre aperta o accordion aperto). -->
<ng-template #entryLinks let-entries>
  @for (entry of entries; track $index) {
    <a class="menu-item" [routerLink]="entry.route" routerLinkActive="is-active" (click)="mobileMenuOpen.set(false)">
      @if (entry.icon) { <app-icon [name]="entry.icon" /> }
      <span class="label">{{ entry.label }}</span>
    </a>
  }
</ng-template>

<!-- Voci della sidebar, condivise tra sidebar flottante (desktop) e drawer (phone).
     Ogni intestazione (gruppo o submenu) è una sezione. Sidebar espansa e drawer: accordion, a meno che la configurazione del menu
     (collapsible) la renda sempre aperta; nel rail: icona con menu a comparsa. Lo stato iniziale viene dalla configurazione (expanded). -->
<ng-template #menuNav>
  @for (node of menuNodes(); track $index) {
    @switch (node.kind) {
      @case ('divider') { <div class="nav-divider"></div> }
      @case ('entry') {
        <a class="menu-item" [routerLink]="node.item.route" routerLinkActive="is-active"
           [attr.title]="railMode() ? node.item.label : null" (click)="mobileMenuOpen.set(false)">
          @if (node.item.icon) { <app-icon [name]="node.item.icon" /> }
          <span class="label">{{ node.item.label }}</span>
        </a>
      }
      @case ('section') {
        @if (!node.entries.length) {
          <!-- intestazione senza voci: nessuna sezione da aprire -->
          <div class="nav-group-label">{{ node.label }}</div>
        } @else if (railMode()) {
          <!-- rail: una sola icona per sezione, le voci si aprono in un menu a comparsa -->
          <button type="button" class="menu-item menu-item--section" [class.is-active]="isSectionActive(node)"
                  nz-popover nzPopoverTrigger="hover" nzPopoverPlacement="rightTop" nzPopoverOverlayClassName="nav-flyout-popover"
                  [nzPopoverTitle]="node.label" [nzPopoverContent]="flyout"
                  [attr.aria-label]="node.label" aria-haspopup="true">
            <app-icon [name]="iconOf(node)" />
          </button>
          <ng-template #flyout>
            <nav class="nav-flyout" [attr.aria-label]="node.label">
              @for (entry of node.entries; track $index) {
                <a class="nav-flyout__item" [routerLink]="entry.route" routerLinkActive="is-active">
                  @if (entry.icon) { <app-icon [name]="entry.icon" [size]="18" /> }
                  <span>{{ entry.label }}</span>
                </a>
              }
            </nav>
          </ng-template>
        } @else if (!optionsOf(node).collapsible) {
          <!-- configurata come non collassabile: etichetta semplice e voci sempre visibili -->
          <div class="nav-group-label">{{ node.label }}</div>
          <div class="nav-group-items" role="group" [attr.aria-label]="node.label">
            <ng-container [ngTemplateOutlet]="entryLinks" [ngTemplateOutletContext]="{ $implicit: node.entries }" />
          </div>
        } @else {
          @let open = isSectionExpanded(node, $index);
          <button type="button" class="nav-group-toggle"
                  [class.is-open]="open" [class.has-active]="isSectionActive(node)"
                  [attr.aria-expanded]="open" [attr.aria-controls]="'nav-group-' + $index"
                  (click)="toggleSection(node, $index)">
            <span class="nav-group-toggle__label">{{ node.label }}</span>
            <app-icon class="nav-group-toggle__chev" name="expand_more" [size]="16" />
          </button>
          @if (open) {
            <div class="nav-group-items" [id]="'nav-group-' + $index" role="group" [attr.aria-label]="node.label">
              <ng-container [ngTemplateOutlet]="entryLinks" [ngTemplateOutletContext]="{ $implicit: node.entries }" />
            </div>
          }
        }
      }
    }
  }
</ng-template>

<!-- ── Topbar (STILE-VISUALE.md §4.1) ─────────────────────────────────────── -->
<header class="topbar">
  <div class="topbar-left">
    @if (isPhone()) {
      <button class="topbar__icon-btn" type="button" aria-label="Apri menu" (click)="mobileMenuOpen.set(true)">
        <app-icon name="menu" />
      </button>
    }
    <a class="brand-chip" routerLink="/">
      <span class="logo-mark"><img src="brand/salutissima-mark.png" alt="" width="24" height="24" /></span>
      <span class="brand-copy">
        <strong>salutissima</strong>
        <small>Gestionale welfare</small>
      </span>
    </a>
  </div>

  <div class="toolbar-actions">
    @if (menuService.documentation().length) {
      <button class="doc-chip" type="button" nz-dropdown nzTrigger="click" nzPlacement="bottomRight"
              [nzDropdownMenu]="docMenu">
        <app-icon name="description" [size]="18" />
        Documentazione
        <app-icon class="chev" name="expand_more" [size]="16" />
      </button>
      <nz-dropdown-menu #docMenu="nzDropdownMenu">
        <ul nz-menu class="topbar-dropdown">
          @for (doc of menuService.documentation(); track $index) {
            <li nz-menu-item [routerLink]="doc.route">
              @if (doc.icon) { <app-icon [name]="doc.icon" [size]="18" /> }
              <span>{{ doc.label }}</span>
            </li>
          }
        </ul>
      </nz-dropdown-menu>
    }

    <app-user-menu
      [user]="authService.currentUser()"
      [roleName]="currentUserRole()"
      [darkTheme]="themeService.isDark()"
      (toggleTheme)="themeService.toggle()" />

    <button class="topbar__icon-btn" type="button" aria-label="Esci" (click)="authService.logout()">
      <app-icon name="logout" />
    </button>
  </div>
</header>

<!-- ── Sidebar flottante (desktop/tablet) ─────────────────────────────────── -->
@if (!isPhone()) {
  <aside class="float-nav" [class.float-nav--collapsed]="isCollapsed()" aria-label="Navigazione principale">
    <div class="sb-head">
      <button class="sb-toggle" type="button" (click)="isCollapsed.set(!isCollapsed())"
              [attr.aria-label]="isCollapsed() ? 'Espandi menu' : 'Comprimi menu'">
        <app-icon [name]="isCollapsed() ? 'chevron_right' : 'chevron_left'" [size]="19" />
      </button>
    </div>
    <ng-container [ngTemplateOutlet]="menuNav" />
  </aside>
}

<!-- ── Contenuto ─────────────────────────────────────────────────────────── -->
<main class="content" [class.content--nav-open]="!isCollapsed() && !isPhone()" [class.content--phone]="isPhone()">
  <router-outlet />
</main>

<!-- ── Drawer menu (phone) ───────────────────────────────────────────────── -->
@if (isPhone()) {
  <nz-drawer
    nzPlacement="left"
    [nzVisible]="mobileMenuOpen()"
    (nzOnClose)="mobileMenuOpen.set(false)"
    nzWidth="264"
    [nzClosable]="false"
    nzWrapClassName="mobile-nav-drawer">
    <ng-template nzDrawerContent>
      <nav class="drawer-nav" aria-label="Navigazione principale">
        <ng-container [ngTemplateOutlet]="menuNav" />
      </nav>
    </ng-template>
  </nz-drawer>
}
